"use client";

import type { RefObject } from "react";
import { Eye, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { Dictionary } from "@/types/i18n";

type CvUploadFields = Dictionary["cvUploadForm"]["fields"];
type CvUploadOptions = Dictionary["cvUploadForm"]["options"];

const ERROR_TEXT_CLASS = "text-[13px] leading-5 text-status-error-text";

interface CvFileFieldProps {
  fields: CvUploadFields;
  file: File | null;
  attemptedSubmit: boolean;
  requiredMessage: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function CvFileField({
  fields,
  file,
  attemptedSubmit,
  requiredMessage,
  inputRef,
  onChange,
}: CvFileFieldProps) {
  const showError = !file && attemptedSubmit;

  return (
    <div className="space-y-2">
      <Label
        htmlFor="cv-file-mobile"
        className="text-[14px] font-medium leading-5 text-text-heading"
      >
        {fields.file.label}
      </Label>
      <div className="flex min-h-11 items-stretch overflow-hidden rounded-[8px] border border-border-input bg-background shadow-button focus-within:border-brand-primary focus-within:ring-4 focus-within:ring-surface-tertiary">
        <label
          htmlFor="cv-file-mobile"
          className="flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-l-[8px] bg-brand-primary pl-4 pr-3 text-sm font-medium leading-5 text-white shadow-[0px_4px_8px_-2px_rgba(10,13,18,0.1),0px_2px_4px_-2px_rgba(10,13,18,0.06)]"
        >
          <Upload className="size-[14px]" />
          {fields.file.placeholder.primary}
        </label>
        <div className="flex min-w-0 flex-1 items-center justify-between gap-2 border-l border-border-input px-[14px] py-1 text-sm leading-5 text-text-muted">
          <span
            className={cn(
              "min-w-0 break-words",
              file ? "font-medium" : "font-normal",
            )}
          >
            {file ? file.name : fields.file.placeholder.hint}
          </span>
          {file ? <Eye className="size-5" aria-hidden /> : null}
        </div>
        <Input
          id="cv-file-mobile"
          type="file"
          accept=".pdf"
          ref={inputRef}
          onChange={onChange}
          className="sr-only"
          aria-describedby={cn(
            "cv-file-mobile-help",
            showError && "cv-file-mobile-error",
          )}
          aria-invalid={showError}
        />
      </div>
      <p id="cv-file-mobile-help" className="text-[13px] leading-5 text-text-muted">
        {fields.file.pdfHint}
      </p>
      {showError && (
        <p id="cv-file-mobile-error" className={ERROR_TEXT_CLASS}>
          {requiredMessage}
        </p>
      )}
    </div>
  );
}

interface CvPurposeFieldProps {
  fields: CvUploadFields;
  options: CvUploadOptions["reviewPurposes"];
  purpose: string;
  attemptedSubmit: boolean;
  requiredMessage: string;
  firstOptionRef: RefObject<HTMLButtonElement | null>;
  onSelect: (value: string) => void;
}

export function CvPurposeField({
  fields,
  options,
  purpose,
  attemptedSubmit,
  requiredMessage,
  firstOptionRef,
  onSelect,
}: CvPurposeFieldProps) {
  const showError = !purpose && attemptedSubmit;

  return (
    <fieldset
      className="space-y-2"
      aria-invalid={showError}
      aria-required="true"
      aria-describedby={cn(
        "cv-purpose-help",
        showError && "cv-purpose-error",
      )}
    >
      <legend
        id="cv-purpose-help"
        className="text-sm font-medium leading-5 text-text-heading"
      >
        {fields.purpose.label}
      </legend>
      <div
        className="flex flex-wrap gap-2"
        role="radiogroup"
        aria-labelledby="cv-purpose-help"
      >
        {options.map((option, index) => {
          const selected = purpose === option.value;
          return (
            <button
              type="button"
              key={option.value}
              ref={index === 0 ? firstOptionRef : undefined}
              onClick={() => onSelect(option.value)}
              className={cn(
                "flex min-h-11 min-w-0 flex-1 items-center justify-center rounded-[8px] border px-[14px] text-sm font-medium leading-5 shadow-button transition-colors",
                selected
                  ? "border-brand-primary bg-status-error-bg"
                  : "border-border-input bg-background",
              )}
              role="radio"
              aria-checked={selected}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {showError && (
        <p id="cv-purpose-error" className={ERROR_TEXT_CLASS}>
          {requiredMessage}
        </p>
      )}
    </fieldset>
  );
}

interface CvExperienceFieldProps {
  fields: CvUploadFields;
  options: CvUploadOptions["experienceLevels"];
  experienceLevel: string;
  attemptedSubmit: boolean;
  requiredMessage: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onChange: (value: string) => void;
}

export function CvExperienceField({
  fields,
  options,
  experienceLevel,
  attemptedSubmit,
  requiredMessage,
  triggerRef,
  onChange,
}: CvExperienceFieldProps) {
  const showError = !experienceLevel && attemptedSubmit;

  return (
    <div className="space-y-2">
      <Label
        htmlFor="mobile-experience-level"
        className="text-sm font-medium leading-5 text-text-heading"
      >
        {fields.experience.label}
      </Label>
      <Select value={experienceLevel} onValueChange={onChange}>
        <SelectTrigger
          id="mobile-experience-level"
          ref={triggerRef}
          aria-required="true"
          aria-invalid={showError}
          aria-describedby={showError ? "cv-experience-error" : undefined}
          className="min-h-11 w-full rounded-[8px] border-border-input bg-background px-[14px] text-base font-medium leading-6 text-text-muted shadow-button focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[placeholder]:text-text-muted data-[state=open]:shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),0px_0px_0px_4px_var(--surface-tertiary)] data-[state=open]:[&_svg]:rotate-180"
        >
          <SelectValue placeholder={fields.experience.placeholder} />
        </SelectTrigger>
        <SelectContent className="w-[var(--radix-select-trigger-width)] rounded-[8px] border-surface-primary shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)]">
          {options.map((level) => (
            <SelectItem
              key={level.value}
              value={level.value}
              className="rounded-none px-[14px] py-[10px] text-sm font-medium text-text-heading data-[highlighted]:bg-surface-primary data-[state=checked]:bg-surface-primary data-[state=checked]:[&_svg]:text-brand-primary"
            >
              {level.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showError && (
        <p id="cv-experience-error" className={ERROR_TEXT_CLASS}>
          {requiredMessage}
        </p>
      )}
    </div>
  );
}
